/**
 * MyPosits.tsx
 * 
 * Unified posits dashboard using ChatLayout for consistent sidebar experience.
 * Shows user's posits with management features in the main content area.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import type { Posit } from "@/types/posit";
import { 
  PositCard, 
  PositSearchBar, 
  PositSortControls, 
  PositFiltersDropdown, 
  PositActiveFilters,
  PositEmptyState,
  PositPaginationControls
} from "@/components/posit/PositList";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PositSelectionModal from "@/components/posit/PositSelectionModal";

const POSITS_PER_PAGE = 10;

const MyPosits = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { handle } = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  
  // State for filters and pagination
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortBy, setSortBy] = useState<"date" | "title">("date");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterPrivacy, setFilterPrivacy] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Check if URL indicates need for Posit selection
  const needsSelection = searchParams.get('select_active') === 'true';

  // Get page owner data
  const { data: pageOwner } = useQuery({
    queryKey: ["user-handle", handle],
    queryFn: async () => {
      if (!handle) return null;

      const { data, error } = await supabase
        .from("users")
        .select("id, first_name, last_name, handle, subscription_type")
        .eq('handle', handle)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!handle
  });

  // Get user profile data
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("users")
        .select("subscription_type, trial_end_date, handle")
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      const isTrialValid = data?.trial_end_date ? new Date(data.trial_end_date) > new Date() : false;
      const isPremiumOrTrial = data?.subscription_type === 'posit_plus' || 
                              (data?.subscription_type === 'trial' && isTrialValid);
      
      return {
        ...data,
        isPremium: isPremiumOrTrial
      };
    },
    enabled: !!user
  });

  const isOwnProfile = !handle || (userProfile?.handle === handle);
  const targetUserId = pageOwner?.id || user?.id;

  // Get posits data
  const { data: posits, isLoading, refetch } = useQuery({
    queryKey: ["user-posits", targetUserId],
    queryFn: async () => {
      if (!targetUserId) throw new Error("No user found");

      const { data, error } = await supabase
        .from("posits")
        .select("*")
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data;
    },
    enabled: !!targetUserId
  });

  // Check if user needs to select active Posit
  const { data: planStatus } = useQuery({
    queryKey: ["plan-status", user?.id],
    queryFn: async () => {
      if (!user?.id || !isOwnProfile) return null;

      const { data, error } = await supabase.functions.invoke('manage-posit-activation', {
        body: { action: 'check_plan_limits' }
      });

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && isOwnProfile
  });

  // Show selection modal when needed
  useEffect(() => {
    if (needsSelection || (planStatus?.needs_selection && isOwnProfile)) {
      setShowSelectionModal(true);
      // Remove the URL parameter
      if (needsSelection) {
        searchParams.delete('select_active');
        setSearchParams(searchParams);
      }
    }
  }, [needsSelection, planStatus, isOwnProfile]);

  // Filter, sort, and paginate posits
  const filteredAndSortedPosits = posits?.filter(posit => {
    // Apply type and privacy filters  
    const typeMatch = filterType === "all" || posit.category === filterType;
    const privacyMatch = filterPrivacy === "all" || posit.privacy === filterPrivacy;
    
    // Apply search filter
    const searchMatch = !searchQuery || 
      posit.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (posit.summary_light && posit.summary_light.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (posit.summary && posit.summary.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return typeMatch && privacyMatch && searchMatch;
  }).sort((a, b) => {
    if (sortBy === "date") {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    } else {
      return sortOrder === "asc" 
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    }
  });

  // Calculate pagination
  const totalPosits = filteredAndSortedPosits?.length || 0;
  const totalPages = Math.ceil(totalPosits / POSITS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSITS_PER_PAGE;
  const endIndex = startIndex + POSITS_PER_PAGE;
  const currentPosits = filteredAndSortedPosits?.slice(startIndex, endIndex) || [];

  // Clear filters function
  const handleClearFilters = () => {
    setFilterType("all");
    setFilterPrivacy("all");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = filterType !== "all" || filterPrivacy !== "all" || searchQuery.trim() !== "";

  const handlePositSelectionComplete = async (selectedPositId: string) => {
    setShowSelectionModal(false);
    // Refresh posits data to show updated active states
    await refetch();
    toast({
      title: "Selection Complete",
      description: "Your selected Posit is now active",
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const displayName = pageOwner 
    ? `${pageOwner.first_name || ''} ${pageOwner.last_name || ''}`.trim() || pageOwner.handle
    : userProfile?.handle || "Your";

  const pageTitle = isOwnProfile ? "Posits" : `${displayName}'s Posits`;
  const pageDescription = isOwnProfile 
    ? "Manage and organize your knowledge bases"
    : `Explore ${displayName}'s public knowledge bases`;

  return (
    <DashboardLayout
      title={pageTitle}
      description={pageDescription}
      headerActions={
        isOwnProfile ? (
          <div className="hidden sm:block">
            <Button asChild className="shrink-0">
              <Link to="/dashboard/create" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Create Posit
              </Link>
            </Button>
          </div>
        ) : undefined
      }
    >
      {/* Selection Modal */}
      {showSelectionModal && posits && posits.length > 1 && isOwnProfile && (
        <PositSelectionModal
          isOpen={showSelectionModal}
          onClose={() => setShowSelectionModal(false)}
          userPosits={posits}
          onSelectionComplete={handlePositSelectionComplete}
        />
      )}

      <div className="space-y-6 relative">
        {/* Plan Status Warning */}
        {planStatus?.needs_selection && isOwnProfile && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="font-medium text-orange-900">Action Required</span>
            </div>
            <p className="text-sm text-orange-700 mb-3">
              Your plan allows {planStatus.allowed_active} active Posit. Please select which one to keep active.
            </p>
            <Button 
              size="sm" 
              onClick={() => setShowSelectionModal(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Select Active Posit
            </Button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 sticky top-0 z-10 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-2">
            <div className="flex-1">
              <PositSearchBar 
                searchQuery={searchQuery} 
                setSearchQuery={setSearchQuery}
                onSearch={() => setCurrentPage(1)}
              />
            </div>
            <div className="flex gap-2">
              <PositSortControls 
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
              />
              <PositFiltersDropdown 
                filterType={filterType}
                setFilterType={setFilterType}
                filterPrivacy={filterPrivacy}
                setFilterPrivacy={setFilterPrivacy}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                clearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>
          </div>

          {/* Active Filters */}
          <PositActiveFilters 
            filterType={filterType}
            filterPrivacy={filterPrivacy}
            searchQuery={searchQuery}
            setFilterType={setFilterType}
            setFilterPrivacy={setFilterPrivacy}
            setSearchQuery={setSearchQuery}
            clearFilters={handleClearFilters}
          />

          {/* Show count when posits exist */}
          {filteredAndSortedPosits?.length > 0 && (
            <div className="text-sm text-muted-foreground mb-4">
              Showing {currentPosits.length} of {totalPosits} Posits
            </div>
          )}

          {/* Empty state */}
          {filteredAndSortedPosits?.length === 0 ? (
            <PositEmptyState 
              hasFilters={hasActiveFilters}
              clearFilters={handleClearFilters}
              isOwner={isOwnProfile}
              isPremium={!!userProfile?.isPremium}
            />
          ) : (
            <>
          {/* Posits List */}
              <div className="space-y-2">
                {currentPosits.map((posit) => (
                  <PositCard 
                    key={posit.id} 
                    posit={posit as any}
                    isOwner={isOwnProfile}
                    isPremium={!!userProfile?.isPremium}
                    variant="compact"
                  />
                ))}
              </div>
              
              {/* Pagination controls */}
              <PositPaginationControls 
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
              />
            </>
          )}
        </div>
        {/* Mobile FAB for Create */}
        {isOwnProfile && (
          <div className="sm:hidden fixed bottom-20 right-5">
            <Button asChild size="lg" className="rounded-full h-12 w-12 p-0 shadow-lg">
              <Link to="/dashboard/create">
                <Plus className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyPosits;
